/**
 * The window layout as plain data, and pure functions to change it.
 *
 * A layout has three parts:
 *
 * - `root`: the **tiled** area, a tree. Leaves are **tab groups** (one or more windows, one of
 *   them shown); inner nodes are **splits** that place their children side by side (`row`) or
 *   one above the other (`column`), with relative `sizes`.
 * - `floating`: windows drawn above the tiles, each with its own position and size.
 * - `closed`: windows that exist but are not shown. Opening one puts it back.
 *
 * Every function returns a **new** layout and never mutates its input, so the window manager
 * can keep history, save to storage, and (later) load layouts from the KDL config.
 */
import { MIN_SHARE } from "./constants";

/** Name of a window, e.g. `graph`. */
export type WindowId = string;

/** A group of windows shown as tabs; `active` is the one visible. */
export interface TabsArea {
  kind: "tabs";
  id: string;
  windows: WindowId[];
  active: WindowId;
}

/** Children placed side by side (`row`) or stacked (`column`). `sizes` are fractions summing to 1. */
export interface SplitArea {
  kind: "split";
  id: string;
  direction: "row" | "column";
  sizes: number[];
  children: Area[];
}

/** A node of the tiled tree. */
export type Area = TabsArea | SplitArea;

/** A window drawn above the tiles, in pixels relative to the workspace. */
export interface FloatingWindow {
  window: WindowId;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The whole layout. */
export interface Layout {
  root: Area | null;
  floating: FloatingWindow[];
  closed: WindowId[];
}

/** Where a dropped window goes relative to a tab group. */
export type DropZone = "center" | "left" | "right" | "top" | "bottom";

/** Counts the areas made so far, for fresh area ids. */
let areaCounter = 0;
/** A fresh area id. */
export function newAreaId(): string {
  areaCounter += 1;
  return `area-${Date.now().toString(36)}-${areaCounter}`;
}

/** Builds a tab group. */
export function tabs(windows: WindowId[], active: WindowId = windows[0] ?? ""): TabsArea {
  return { kind: "tabs", id: newAreaId(), windows, active };
}

/** Builds a split; sizes default to equal shares. */
export function split(direction: "row" | "column", children: Area[], sizes?: number[]): SplitArea {
  return { kind: "split", id: newAreaId(), direction, children, sizes: sizes ?? children.map(() => 1 / children.length) };
}

/** A deep copy, so the functions below can edit freely. */
function copy<T>(value: T): T {
  return structuredClone(value);
}

/** Every window shown in the tiles, in tree order. */
export function tiledWindows(area: Area | null): WindowId[] {
  if (!area) return [];
  return area.kind === "tabs" ? [...area.windows] : area.children.flatMap(tiledWindows);
}

/** Where a window is: tiled, floating, closed, or not in the layout at all. */
export function placeOf(layout: Layout, window: WindowId): "tiled" | "floating" | "closed" | null {
  if (tiledWindows(layout.root).includes(window)) return "tiled";
  if (layout.floating.some((f) => f.window === window)) return "floating";
  if (layout.closed.includes(window)) return "closed";
  return null;
}

/** `true` if the window is visible (tiled or floating). */
export function isOpen(layout: Layout, window: WindowId): boolean {
  const place = placeOf(layout, window);
  return place === "tiled" || place === "floating";
}

/** Finds an area by id. */
export function findArea(area: Area | null, id: string): Area | null {
  if (!area) return null;
  if (area.id === id) return area;
  if (area.kind === "split") {
    for (const child of area.children) {
      const found = findArea(child, id);
      if (found) return found;
    }
  }
  return null;
}

/** The tab group holding `window`, if it is tiled. */
export function areaOf(area: Area | null, window: WindowId): TabsArea | null {
  if (!area) return null;
  if (area.kind === "tabs") return area.windows.includes(window) ? area : null;
  for (const child of area.children) {
    const found = areaOf(child, window);
    if (found) return found;
  }
  return null;
}

/**
 * Cleans a tree after an edit: removes empty tab groups, replaces splits with a single child
 * by that child, merges a split into its parent when they go the same direction, and
 * rescales sizes so they sum to 1.
 */
export function normalize(area: Area | null): Area | null {
  if (!area) return null;
  if (area.kind === "tabs") {
    if (!area.windows.length) return null;
    if (!area.windows.includes(area.active)) area.active = area.windows[0]!;
    return area;
  }
  const children: Area[] = [];
  const sizes: number[] = [];
  area.children.forEach((child, i) => {
    const clean = normalize(child);
    if (!clean) return;
    const share = area.sizes[i] ?? 1 / area.children.length;
    if (clean.kind === "split" && clean.direction === area.direction) {
      clean.children.forEach((grandchild, j) => {
        children.push(grandchild);
        sizes.push(share * (clean.sizes[j] ?? 0));
      });
    } else {
      children.push(clean);
      sizes.push(share);
    }
  });
  if (!children.length) return null;
  if (children.length === 1) return children[0]!;
  const total = sizes.reduce((a, b) => a + b, 0) || 1;
  area.children = children;
  area.sizes = sizes.map((s) => s / total);
  return area;
}

/** Takes `window` out of wherever it is (tiles, floating or closed). */
export function detach(layout: Layout, window: WindowId): Layout {
  const next = copy(layout);
  /** Removes the window from this area and its children. */
  const strip = (area: Area | null): void => {
    if (!area) return;
    if (area.kind === "tabs") area.windows = area.windows.filter((w) => w !== window);
    else area.children.forEach(strip);
  };
  strip(next.root);
  next.root = normalize(next.root);
  next.floating = next.floating.filter((f) => f.window !== window);
  next.closed = next.closed.filter((w) => w !== window);
  return next;
}

/**
 * Puts `window` next to (or into) the tab group `areaId`. `center` adds it as a tab; the
 * other zones split the group and put the window on that side, taking `share` of the space
 * (half by default).
 * If the area does not exist (or the tiles are empty), the window becomes the whole tiled area.
 */
export function dock(layout: Layout, window: WindowId, areaId: string, zone: DropZone, share = 0.5): Layout {
  const next = detach(layout, window);
  const target = findArea(next.root, areaId);
  if (!next.root || !target || target.kind !== "tabs") {
    next.root = next.root ? normalize(split("row", [next.root, tabs([window])], [0.7, 0.3])) : tabs([window]);
    return next;
  }
  if (zone === "center") {
    target.windows.push(window);
    target.active = window;
    return next;
  }
  const placed = tabs([window]);
  const direction = zone === "left" || zone === "right" ? "row" : "column";
  const first = zone === "left" || zone === "top";
  const part = Math.min(0.9, Math.max(0.1, share));
  const replacement = split(direction, first ? [placed, { ...target }] : [{ ...target }, placed], first ? [part, 1 - part] : [1 - part, part]);
  next.root = normalize(replaceArea(next.root, areaId, replacement));
  return next;
}

/** Replaces the area `id` by `replacement` in the tree. */
function replaceArea(area: Area, id: string, replacement: Area): Area {
  if (area.id === id) return replacement;
  if (area.kind === "split") area.children = area.children.map((c) => replaceArea(c, id, replacement));
  return area;
}

/** Makes `window` float at `rect`. */
export function float(layout: Layout, window: WindowId, rect: Omit<FloatingWindow, "window">): Layout {
  const next = detach(layout, window);
  next.floating.push({ window, ...rect });
  return next;
}

/** Moves or resizes a floating window. Does nothing if it is not floating. */
export function moveFloating(layout: Layout, window: WindowId, rect: Partial<Omit<FloatingWindow, "window">>): Layout {
  const next = copy(layout);
  next.floating = next.floating.map((f) => (f.window === window ? { ...f, ...rect } : f));
  return next;
}

/** Brings a floating window above the others (the last one is drawn on top). */
export function raise(layout: Layout, window: WindowId): Layout {
  const index = layout.floating.findIndex((f) => f.window === window);
  if (index < 0 || index === layout.floating.length - 1) return layout;
  const next = copy(layout);
  const [entry] = next.floating.splice(index, 1);
  next.floating.push(entry!);
  return next;
}

/** Hides `window`; it can be opened again later. */
export function close(layout: Layout, window: WindowId): Layout {
  const next = detach(layout, window);
  next.closed.push(window);
  return next;
}

/**
 * Shows `window` again: as the shown tab of the last tab group (the side panel), or as the whole tiled area if
 * the tiles are empty. Does nothing if it is already open.
 */
export function open(layout: Layout, window: WindowId): Layout {
  if (isOpen(layout, window)) return layout;
  const next = detach(layout, window);
  const last = lastTabs(next.root);
  return last ? dock(next, window, last.id, "center") : { ...next, root: tabs([window]) };
}

/** The last tab group in tree order (the side panel of a main view and a side panel). */
function lastTabs(area: Area | null): TabsArea | null {
  if (!area) return null;
  return area.kind === "tabs" ? area : lastTabs(area.children[area.children.length - 1] ?? null);
}

/** Shows the tab `window` in its group. */
export function activate(layout: Layout, window: WindowId): Layout {
  const next = copy(layout);
  const area = areaOf(next.root, window);
  if (area) area.active = window;
  return next;
}

/** Changes the sizes of split `id`. Each share is kept at least {@link MIN_SHARE}. */
export function resize(layout: Layout, id: string, sizes: number[]): Layout {
  const next = copy(layout);
  const area = findArea(next.root, id);
  if (area?.kind !== "split" || sizes.length !== area.children.length) return layout;
  const clamped = sizes.map((s) => Math.max(MIN_SHARE, s));
  const total = clamped.reduce((a, b) => a + b, 0);
  area.sizes = clamped.map((s) => s / total);
  return next;
}

/**
 * Makes a saved layout safe to use with the windows that exist now: unknown windows are
 * dropped (unless `keepUnknown` says to keep them: windows that may appear later), duplicates
 * removed, and windows missing from the layout are added as closed.
 */
export function sanitize(layout: Layout, known: WindowId[], keepUnknown: (w: WindowId) => boolean = () => false): Layout {
  const next = copy(layout);
  const seen = new Set<WindowId>();
  /** `true` the first time a known (or kept) window is seen. */
  const keep = (w: WindowId) => (known.includes(w) || keepUnknown(w)) && !seen.has(w) && (seen.add(w), true);
  /** Drops unknown and duplicate windows from this area and its children. */
  const clean = (area: Area | null): void => {
    if (!area) return;
    if (area.kind === "tabs") area.windows = area.windows.filter(keep);
    else area.children.forEach(clean);
  };
  clean(next.root);
  next.root = normalize(next.root);
  next.floating = next.floating.filter((f) => keep(f.window));
  next.closed = next.closed.filter(keep);
  for (const w of known) if (!seen.has(w)) next.closed.push(w);
  return next;
}

/** Every open window in the order a compact screen lists them: the tiles, then the floating ones. */
export function openWindows(layout: Layout): WindowId[] {
  return [...tiledWindows(layout.root), ...layout.floating.map((f) => f.window)];
}

/**
 * The window a compact screen shows after the open windows went from `before` to `after`: one
 * that just opened (the last of them), else the one shown so far while it stays open, else the
 * graph, else the first.
 */
export function compactShown(shown: WindowId | null, before: WindowId[], after: WindowId[]): WindowId | null {
  const opened = after.filter((w) => !before.includes(w));
  if (opened.length > 0 && before.length > 0) return opened[opened.length - 1]!;
  if (shown && after.includes(shown)) return shown;
  if (after.includes("graph")) return "graph";
  return after[0] ?? null;
}
